import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../components/layout';
import { FileText, ClipboardCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { facultyExamApi } from '../api/examApi';
import { toast } from 'sonner';

export default function DashboardPage() {
  const { user } = useAuth();
  const [activeExams, setActiveExams] = useState(null);
  const [pendingEvals, setPendingEvals] = useState(null);

  useEffect(() => {
    facultyExamApi.getMyExams()
      .then((res) => {
        const published = res.data.filter((e) => e.is_published === 'published');
        setActiveExams(published.length);
      })
      .catch(() => toast.error('Failed to load exam count'));

    facultyExamApi.getPendingEvaluations()
      .then((res) => setPendingEvals(res.data.length))
      .catch(() => toast.error('Failed to load evaluations count'));
  }, []);

  return (
    <DashboardLayout title="Faculty Dashboard">
      <div className="animate-fade-in">
        <div className="mb-8">
          <h2 className="text-2xl font-heading font-bold text-slate-900 dark:text-white">
            Welcome back, {user?.name || 'Faculty'}!
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Future Makers - Educational Institute Management System
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Active Exams */}
          <div
            className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6 hover:shadow-md transition-shadow"
            data-testid="stat-card-active-exams"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Active Exams</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">
                  {activeExams ?? '…'}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-emerald-500">
                <FileText className="text-white" size={24} />
              </div>
            </div>
          </div>

          {/* Pending Evaluations — prominent, click-through */}
          <Link
            to="/faculty/evaluations"
            className="block bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6 hover:shadow-md hover:border-amber-400 transition-all"
            data-testid="stat-card-pending-evaluations"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Pending Evaluations</p>
                <p className={`text-3xl font-bold mt-1 ${
                  pendingEvals > 0
                    ? 'text-amber-600 dark:text-amber-400'
                    : 'text-slate-900 dark:text-white'
                }`}>
                  {pendingEvals ?? '…'}
                </p>
                {pendingEvals > 0 && (
                  <p className="text-xs text-amber-500 mt-1">Click to evaluate →</p>
                )}
              </div>
              <div className="p-3 rounded-lg bg-amber-500">
                <ClipboardCheck className="text-white" size={24} />
              </div>
            </div>
          </Link>
        </div>

        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
            Quick Actions
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Link
              to="/faculty/exams/create"
              className="flex items-center gap-3 p-4 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-primary dark:hover:border-accent-cool hover:bg-primary/5 dark:hover:bg-accent-cool/5 transition-colors"
              data-testid="quick-action-create-exam"
            >
              <div className="p-2 bg-emerald-500/10 rounded-lg">
                <FileText className="text-emerald-600" size={20} />
              </div>
              <div>
                <p className="font-medium text-slate-900 dark:text-white">Create Exam</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">New test</p>
              </div>
            </Link>
            <Link
              to="/faculty/evaluations"
              className="flex items-center gap-3 p-4 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-primary dark:hover:border-accent-cool hover:bg-primary/5 dark:hover:bg-accent-cool/5 transition-colors"
              data-testid="quick-action-evaluations"
            >
              <div className="p-2 bg-amber-500/10 rounded-lg">
                <ClipboardCheck className="text-amber-600" size={20} />
              </div>
              <div>
                <p className="font-medium text-slate-900 dark:text-white">Evaluations</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Grade submissions</p>
              </div>
            </Link>
            <Link
              to="/faculty/exams"
              className="flex items-center gap-3 p-4 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-primary dark:hover:border-accent-cool hover:bg-primary/5 dark:hover:bg-accent-cool/5 transition-colors"
              data-testid="quick-action-view-exams"
            >
              <div className="p-2 bg-secondary/10 rounded-lg">
                <FileText className="text-secondary" size={20} />
              </div>
              <div>
                <p className="font-medium text-slate-900 dark:text-white">My Exams</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Manage tests</p>
              </div>
            </Link>
          </div>
        </div>

        <div className="mt-8 p-4 bg-accent-warm/10 dark:bg-accent-warm/5 border border-accent-warm/30 rounded-lg">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            <span className="font-semibold text-secondary">Future Makers v2.0:</span> Faculty Management + Student Exam Portal now active!
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
}
